// CommercialDetails.jsx
import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { URL, getToken, getLocalDate } from "../../utils";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import ChartFullBg from "../../components/ChartFullBg";
import ProspectStatsChart from "../../components/ProspectChart";
import ConditionalRender from "../../components/ConditionalRender";

export default function CommercialDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = getToken();
  const [commercial, setCommercial] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [prospectsData, setProspectsData] = useState({
    week: [],
    month: [],
    year: [],
  });
  const [statisticData, setStatisticData] = useState({
    week: [],
    month: [],
    year: [],
  });

  const [selectedPeriod, setSelectedPeriod] = useState("month");

  useEffect(() => {
    fetchCommercialDetails();
  }, [id, token, navigate]);

  const fetchCommercialDetails = async () => {
    try {
      setLoading(true);
      const [
        detailsResponse,
        processDataWeekResponse,
        prospectsDataMonthResponse,
        prospectsDataYearResponse,
        weekDataResponse,
        monthDataResponse,
        yearDataResponse,
        statsResponse,
      ] = await Promise.all([
        axios.get(`${URL}Magasins/commercial/detail-commercial/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }),
        axios.get(`${URL}Magasins/nombre-prospects-commerciaux_w/${id}`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }),
        axios.get(`${URL}Magasins/nombre-prospects-commerciaux_m/${id}`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }),
        axios.get(`${URL}Magasins/nombre-prospects-commerciaux_y/${id}`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }),
        axios.get(`${URL}Declarations/chiffres-affaire-commerciaux_w/${id}`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }),
        axios.get(`${URL}Declarations/chiffres-affaire-commerciaux_m/${id}`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }),
        axios.get(`${URL}Declarations/chiffres-affaire-commerciaux_y/${id}`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }),
        axios.get(`${URL}Magasins/commercial/stats-commercial/${id}`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }),
      ]);

      setCommercial(detailsResponse.data);

      const weekTemp = { data: [], labels: [] };
      // const uniteData = { labels: [], values: [] };
      processDataWeekResponse.data.ventes_par_periode.forEach((item) => {
        weekTemp.data.push(item.total);
        weekTemp.labels.push(getLocalDate(new Date(item.date_creation__date)));
      });

      const monthTemp = { data: [], labels: [] };
      // const uniteData = { labels: [], values: [] };
      prospectsDataMonthResponse.data.ventes_par_periode.forEach((item) => {
        monthTemp.data.push(item.total);
        // get the mouth oft the date in FR format
        monthTemp.labels.push(getLocalDate(new Date(item.date_creation__date)));
      });

      const yearTemp = { data: [], labels: [] };
      // const uniteData = { labels: [], values: [] };
      prospectsDataYearResponse.data.ventes_par_periode.forEach((item) => {
        yearTemp.data.push(item.total);
        yearTemp.labels.push(
          new Date(item.mois).toLocaleString("fr-FR", {
            month: "long",
          })
        );
      });

      setProspectsData({
        week: weekTemp,
        month: monthTemp,
        year: yearTemp,
      });

      setStats(statsResponse.data);

      const weekStatsTmp = { data: [], labels: [] };
      weekDataResponse.data.ventes_par_periode.forEach((item) => {
        weekStatsTmp.data.push(item.total);
        weekTemp.labels.push(getLocalDate(item.date_sortie_date));
      });
      const monthStatsTmp = { data: [], labels: [] };
      monthDataResponse.data.ventes_par_periode.forEach((item) => {
        monthStatsTmp.data.push(item.total);
        monthStatsTmp.labels.push(getLocalDate(item.date_sortie_date));
      });

      const yearStatsTmp = { data: [], labels: [] };
      yearDataResponse.data.ventes_par_periode.forEach((item) => {
        yearStatsTmp.data.push(item.total);
        yearStatsTmp.labels.push(
          new Date(item.mois).toLocaleString("fr-FR", {
            month: "long",
          })
        );
      });

      setStatisticData({
        week: weekStatsTmp,
        month: monthStatsTmp,
        year: yearStatsTmp,
      });
    } catch (error) {
      toast.error("Erreur lors du chargement des détails du commercial");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  if (!commercial) {
    return <div className="p-4 text-center">Commercial non trouvé</div>;
  }

  return (
    <>
      <ToastContainer />
      <div className="p-4 mx-auto">
        <div className="flex flex-col lg:flex-row-reverse gap-6">
          {/* Section droite - Informations du commercial */}
          <div className="w-full lg:w-1/3 bg-white shadow-md rounded-lg overflow-hidden">
            <div className="bg-amber-600 p-4 text-white">
              <div className="flex items-center gap-4">
                <div className="p-2 bg-amber-500 rounded-full">
                  <svg
                    className="w-8 h-8"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                </div>
                <div>
                  <h1 className="text-xl font-bold">
                    {commercial.prenom} {commercial.nom}
                  </h1>
                  <p className="text-amber-100">
                    {commercial.statut === "Actif" ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Actif
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        Inactif
                      </span>
                    )}
                  </p>
                </div>
              </div>
            </div>

            <div className="p-4">
              <div className="mb-6">
                <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <svg
                    className="w-5 h-5 text-amber-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                  Informations personnelles
                </h2>
                <div className="space-y-4 p-2 border border-gray-100 bg-gray-100 rounded-md">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg text-blue-600">
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                        />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Téléphone</p>
                      <p className="font-medium text-gray-800">
                        {commercial.telephone || "-"}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-teal-100 rounded-lg text-teal-600">
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          stroke="currentColor"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M4 10h16m-8-3V4M7 7V4m10 3V4M5 20h14a1 1 0 0 0 1-1V7a1 1 0 0 0-1-1H5a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1Zm3-7h.01v.01H8V13Zm4 0h.01v.01H12V13Zm4 0h.01v.01H16V13Zm-8 4h.01v.01H8V17Zm4 0h.01v.01H12V17Zm4 0h.01v.01H16V17Z"
                        />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Date de naissance</p>
                      <p className="font-medium text-gray-800">
                        {commercial.date_naissance || "-"}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-purple-100 rounded-lg text-purple-600">
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"
                        />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Sexe</p>
                      <p className="font-medium text-gray-800">
                        {commercial.sexe}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-rose-100 rounded-lg text-rose-600">
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          stroke="currentColor"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Zm0 0a8.949 8.949 0 0 0 4.951-1.488A3.987 3.987 0 0 0 13 16h-2a3.987 3.987 0 0 0-3.951 3.512A8.948 8.948 0 0 0 12 21Zm3-11a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
                        />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">
                        Situation matrimoniale
                      </p>
                      <p className="font-medium text-gray-800">
                        {commercial.situation_mat || "-"}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg text-blue-600">
                      <svg
                        className="w-6 h-6"
                        aria-hidden="true"
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke="currentColor"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M12 13a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z"
                        />
                        <path
                          stroke="currentColor"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M17.8 13.938h-.011a7 7 0 1 0-11.464.144h-.016l.14.171c.1.127.2.251.3.371L12 21l5.13-6.248c.194-.209.374-.429.54-.659l.13-.155Z"
                        />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Pays</p>
                      <p className="font-medium text-gray-800">
                        {commercial.country || commercial.pays || "-"}
                      </p>
                    </div>
                  </div>

                  {/* Address */}
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-orange-100 rounded-lg text-orange-600">
                      <svg
                        className="w-6 h-6"
                        aria-hidden="true"
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke="currentColor"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="m4 12 8-8 8 8M6 10.5V19a1 1 0 0 0 1 1h3v-3a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v3h3a1 1 0 0 0 1-1v-8.5"
                        />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Adresse</p>
                      <p className="font-medium text-gray-800">
                        {commercial.adresse || "-"}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-green-100 rounded-lg text-green-600">
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                        />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Magasin</p>
                      <p className="font-medium text-gray-800">
                        {commercial.magasin_name || "Non attribué"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <svg
                    className="w-5 h-5 text-amber-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                    />
                  </svg>
                  Objectifs
                </h2>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-amber-50 p-4 rounded-lg border border-amber-100">
                    <div className="flex items-center gap-2 mb-1">
                      <svg
                        className="w-5 h-5 text-amber-600"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <p className="text-xs font-medium text-amber-600">
                        Journalier
                      </p>
                    </div>
                    <p className="text-xl font-bold text-amber-800">
                      {commercial.objectif_journalier.toLocaleString()}
                    </p>
                  </div>
                  <div className="bg-amber-50 p-4 rounded-lg border border-amber-100">
                    <div className="flex items-center gap-2 mb-1">
                      <svg
                        className="w-5 h-5 text-amber-600"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                      <p className="text-xs font-medium text-amber-600">
                        Mensuel
                      </p>
                    </div>
                    <p className="text-xl font-bold text-amber-800">
                      {commercial.objectif_mensuel.toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
              <div className="mb-6">
                <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <svg
                    className="w-5 h-5 text-amber-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M8.891 15.107 15.11 8.89m-5.183-.52h.01m3.089 7.254h.01M14.08 3.902a2.849 2.849 0 0 0 2.176.902 2.845 2.845 0 0 1 2.94 2.94 2.849 2.849 0 0 0 .901 2.176 2.847 2.847 0 0 1 0 4.16 2.848 2.848 0 0 0-.901 2.175 2.843 2.843 0 0 1-2.94 2.94 2.848 2.848 0 0 0-2.176.902 2.847 2.847 0 0 1-4.16 0 2.85 2.85 0 0 0-2.176-.902 2.845 2.845 0 0 1-2.94-2.94 2.848 2.848 0 0 0-.901-2.176 2.848 2.848 0 0 1 0-4.16 2.849 2.849 0 0 0 .901-2.176 2.845 2.845 0 0 1 2.941-2.94 2.849 2.849 0 0 0 2.176-.901 2.847 2.847 0 0 1 4.159 0Z"
                    />
                  </svg>
                  Quota
                </h2>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                    <div className="flex items-center gap-2 mb-1">
                      <svg
                        className="w-5 h-5 text-blue-600"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <p className="text-xs font-medium text-blue-600">
                        Journalier
                      </p>
                    </div>
                    <p className="text-xl font-bold text-blue-800">
                      {stats.quota_journalier.toFixed(0)}%
                    </p>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                    <div className="flex items-center gap-2 mb-1">
                      <svg
                        className="w-5 h-5 text-blue-600"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                      <p className="text-xs font-medium text-blue-600">
                        Mensuel
                      </p>
                    </div>
                    <p className="text-xl font-bold text-blue-800">
                      {stats.quota_mensuel.toFixed(0)}%
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col space-y-3">
                <ConditionalRender requiredPermissions={["canEditCommercial"]}>
                  <Link
                    to={`/commercials/edit/${id}`}
                    className="flex items-center justify-center gap-2 text-white bg-amber-600 hover:bg-amber-700 px-4 py-2.5 rounded-lg transition"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                      />
                    </svg>
                    Modifier
                  </Link>
                </ConditionalRender>

                <Link
                  to="/commercials"
                  className="flex items-center justify-center gap-2 text-gray-700 border border-gray-300 hover:bg-gray-50 px-4 py-2.5 rounded-lg transition"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M10 19l-7-7m0 0l7-7m-7 7h18"
                    />
                  </svg>
                  Retour à la liste
                </Link>
              </div>
            </div>
          </div>

          {/* Section gauche - Statistiques */}
          <div className="w-full lg:w-2/3 space-y-6">
            {/* Carte de performance */}
            <div className="bg-white shadow-md rounded-lg p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">
                Performance du commercial
              </h2>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-blue-50 p-3 rounded-lg text-sm">
                  <p className="text-blue-600">Personnes rencontrées</p>
                  <p className="font-bold text-blue-800 mt-2">
                    {stats.personnes_jour.toLocaleString()}
                  </p>
                </div>
                <div className="bg-green-50 p-3 rounded-lg text-sm">
                  <p className=" text-green-600">Entité rencontrées</p>
                  <p className="font-bold text-green-800 mt-2">
                    {stats.entite_jour.toLocaleString()}
                  </p>
                </div>
                <div className="bg-purple-50 p-3 rounded-lg text-sm">
                  <p className="text-purple-600">CA d'aujourd'hui</p>
                  <p className="font-bold text-purple-800 mt-2">
                    {stats.chiffre_affaire_jour.toLocaleString()}
                  </p>
                </div>
                <div className="bg-amber-50 p-3 rounded-lg text-sm">
                  <p className="text-amber-600">CA mensuel</p>
                  <p className="font-bold text-amber-800 mt-2">
                    {stats.chiffre_affaire_mois.toLocaleString()}
                  </p>
                </div>
              </div>
            </div>

            {/* Graphique ApexCharts */}
            <ProspectStatsChart prospectsData={prospectsData} />

            <div className="mx-auto w-full">
              <div className="grid gap-4 p-4 bg-white shadow-md rounded-md">
                <div className="flex max-sm:flex-col sm:items-center sm:justify-between">
                  <h2 className="text-2xl font-semibold">
                    {
                      {
                        week: "Chiffre d'affaire du commercial durant la semaine",
                        month: "Chiffre d'affaire du commercial durant le mois",
                        year: "Chiffre d'affaire du commercial durant l'année",
                      }[selectedPeriod] // Récupère le titre en fonction de la période sélectionnée
                    }
                  </h2>
                  <select
                    className="w-max p-2 border-2 rounded-md bg-gray-50 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    name="selectedPeriod"
                    id="selectedPeriod"
                    value={selectedPeriod}
                    onChange={(e) => {
                      setSelectedPeriod(e.target.value);
                    }}
                  >
                    <option value="week">Semaine</option>
                    <option value="month">Mois</option>
                    <option value="year">Année</option>
                  </select>
                </div>
                {
                  {
                    week: (
                      <ChartFullBg
                        key={"week-" + Math.random()}
                        data={statisticData.week.data}
                        dataLabels={statisticData.week.labels}
                        colors={["#f97316"]}
                        title="Historique des achats de la semaine"
                      />
                    ),
                    month: (
                      <ChartFullBg
                        key={"month-" + Math.random()}
                        data={statisticData.month.data}
                        dataLabels={statisticData.month.labels}
                        colors={["#fbbf24"]}
                        title="Historique des achats du mois"
                      />
                    ),
                    year: (
                      <ChartFullBg
                        key={"year-" + Math.random()}
                        data={statisticData.year.data}
                        dataLabels={statisticData.year.labels}
                        colors={["#84cc16"]}
                        title="Historique des achats de l'année"
                      />
                    ),
                  }[selectedPeriod]
                }
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
